import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Shield, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Auth callback page — receives `#session_id=...` in the URL fragment
 * from Emergent's hosted Google auth and exchanges it for our own
 * JWT via the backend.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR
 * REDIRECT URLS, THIS BREAKS THE AUTH.
 *
 * The redirect_origin was passed to Emergent dynamically at click
 * time using `window.location.origin`, so we just need to read the
 * fragment here and post it to the backend.
 */
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  // useRef (not state) — set synchronously to prevent the
  // double-exchange race condition that StrictMode + React 18
  // causes when useEffect fires twice.
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : "";

    if (!sessionId) {
      // No handshake token — bounce to login with an error banner.
      navigate("/login?error=google_no_session", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/auth/google/callback`,
          { handshake_token: sessionId, provider: "emergent" },
        );
        // Match the shape /api/auth/login returns so AuthContext's
        // setSession handles both flows identically.
        setSession({
          token: data.token,
          user: data.user,
          trial: data.trial,
        });
        const target = data.user?.is_admin ? "/admin" : "/dashboard";
        navigate(target, { replace: true });
      } catch (err) {
        const code = err?.response?.status || 0;
        const detail =
          err?.response?.data?.detail || "google_signin_failed";
        // Encode the detail so suspended/deactivated/under_review
        // accounts can show the exact backend message on the login
        // page, matching the email/password flow.
        navigate(
          `/login?error=${encodeURIComponent(
            `google:${code}:${detail}`,
          )}`,
          { replace: true },
        );
      }
    })();
  }, [navigate, setSession]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#0B1120] p-6"
      data-testid="auth-callback-page"
    >
      <div className="w-full max-w-sm text-center">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
            <Shield className="w-6 h-6 text-slate-900" />
          </div>
          <span className="font-barlow font-bold text-xl text-slate-50">
            Sentient Vault
          </span>
        </div>
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin mx-auto mb-4" />
        <p className="text-sm text-slate-400">
          Signing you in with Google…
        </p>
      </div>
    </div>
  );
}
